import { useEffect, useRef, useState } from 'react';
import { FilterX, Search } from 'lucide-react';
import { Button, FormField, Input, Select, StatusPill } from '@/components/ui';
import { ADMIN_SEARCH_DEBOUNCE_MS } from './admin-list-params';
import {
  DEFAULT_FRAME_REVIEW_FILTERS,
  FRAME_REVIEW_STATUS_FILTERS,
  hasActiveFrameReviewFilters,
  isValidFrameReviewDays,
  type FrameReviewFilters,
  type FrameReviewStatusFilter,
} from './frameReviewFilters';
import type { FrameCalibrationFilter, FrameChangeFilter } from '@/api/admin';

const STATUS_LABEL: Record<FrameReviewStatusFilter, string> = {
  active: 'Nur aktive',
  inactive: 'Nur deaktivierte',
  all: 'Alle Produkte',
};

const CHANGED_LABEL: Record<'any' | FrameChangeFilter, string> = {
  any: 'Egal',
  within: 'Vor weniger als … Tagen',
  before: 'Vor mehr als … Tagen',
  never: 'Noch nie',
};
const CHANGED_OPTIONS: Array<'any' | FrameChangeFilter> = [
  'any',
  'within',
  'before',
  'never',
];

const CALIBRATION_LABEL: Record<'any' | FrameCalibrationFilter, string> = {
  open: 'Offene Vorlagen',
  any: 'Alle Vorlagen',
  uncalibrated: 'Standard unkalibriert',
  inconsistent: 'Standard „prüfen“',
};
const CALIBRATION_OPTIONS: Array<'any' | FrameCalibrationFilter> = [
  'open',
  'any',
  'uncalibrated',
  'inconsistent',
];

interface FrameReviewFilterBarProps {
  filters: FrameReviewFilters;
  /**
   * Applies a partial change. Returns false when the page declined it (the
   * admin kept unsaved frame edits); the typed fields then fall back to the
   * applied value.
   */
  onChange: (patch: Partial<FrameReviewFilters>) => boolean;
  /** Products in the queue the filters produce. */
  total: number;
  /** Templates still to calibrate across those products; omitted = hidden. */
  openTemplates?: number;
  /** The queue is being reloaded for new filters. */
  fetching?: boolean;
}

/**
 * Filters of the frame-review walkthrough. Selects write at once; the
 * typed fields (search, day window) wait for a pause, so the queue is not
 * rebuilt on every keystroke. Each change is a patch, and the timers read
 * the current filters through a ref, so a select changed during the pause
 * is never overwritten with the filters of an earlier render.
 */
export function FrameReviewFilterBar({
  filters,
  onChange,
  total,
  openTemplates,
  fetching = false,
}: FrameReviewFilterBarProps) {
  const [searchInput, setSearchInput] = useState(filters.search);
  const [daysInput, setDaysInput] = useState(String(filters.days));

  const filtersRef = useRef(filters);
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    filtersRef.current = filters;
    onChangeRef.current = onChange;
  });

  // Follow the URL when it changes from outside the inputs (link, back).
  useEffect(() => setSearchInput(filters.search), [filters.search]);
  useEffect(() => setDaysInput(String(filters.days)), [filters.days]);

  useEffect(() => {
    const search = searchInput.trim();
    if (search === filtersRef.current.search) return;
    const timer = setTimeout(() => {
      if (search === filtersRef.current.search) return;
      if (!onChangeRef.current({ search })) {
        setSearchInput(filtersRef.current.search);
      }
    }, ADMIN_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // An empty or invalid window (while typing) is left alone; only a usable
  // number reaches the URL. Blur settles whatever is left in the field.
  useEffect(() => {
    const trimmed = daysInput.trim();
    if (!isValidFrameReviewDays(trimmed)) return;
    const days = Number(trimmed);
    if (days === filtersRef.current.days) return;
    const timer = setTimeout(() => {
      if (days === filtersRef.current.days) return;
      if (!onChangeRef.current({ days })) {
        setDaysInput(String(filtersRef.current.days));
      }
    }, ADMIN_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [daysInput]);

  const settleDays = () => {
    const trimmed = daysInput.trim();
    if (isValidFrameReviewDays(trimmed)) {
      const days = Number(trimmed);
      if (days !== filters.days && onChange({ days })) return;
    }
    setDaysInput(String(filters.days));
  };

  const windowActive =
    filters.changed === 'within' || filters.changed === 'before';
  const filtered = hasActiveFrameReviewFilters(filters);

  return (
    <div className="mb-3 rounded-card p-3 ring-1 ring-line">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <FormField label="Status">
          <Select
            value={filters.status}
            onChange={(e) =>
              onChange({ status: e.target.value as FrameReviewStatusFilter })
            }
          >
            {FRAME_REVIEW_STATUS_FILTERS.map((option) => (
              <option key={option} value={option}>
                {STATUS_LABEL[option]}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Rahmen geändert">
          <Select
            value={filters.changed}
            onChange={(e) =>
              onChange({
                changed: e.target.value as 'any' | FrameChangeFilter,
              })
            }
          >
            {CHANGED_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {CHANGED_LABEL[option]}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField
          label="Tage"
          hint={windowActive ? undefined : 'Gilt mit „vor … Tagen“'}
        >
          <Input
            type="number"
            inputMode="numeric"
            min={1}
            max={3650}
            value={daysInput}
            disabled={!windowActive}
            onChange={(e) => setDaysInput(e.target.value)}
            onBlur={settleDays}
          />
        </FormField>
        <FormField label="Kalibrierung">
          <Select
            value={filters.calibration}
            onChange={(e) =>
              onChange({
                calibration: e.target.value as 'any' | FrameCalibrationFilter,
              })
            }
          >
            {CALIBRATION_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {CALIBRATION_LABEL[option]}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Suche">
          <Input
            type="search"
            icon={Search}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Name oder SKU"
          />
        </FormField>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 text-xs text-mute">
        <span className="flex items-center gap-2">
          <span aria-live="polite">
            {fetching
              ? 'Warteschlange wird neu aufgebaut…'
              : `${total} ${total === 1 ? 'Produkt' : 'Produkte'} im Durchlauf`}
          </span>
          {openTemplates != null && !fetching && (
            <StatusPill
              variant={openTemplates > 0 ? 'warning' : 'published'}
              title="Vorlagen, die noch kalibriert werden müssen (unkalibriert oder prüfen)"
              data-testid="open-count"
            >
              {openTemplates} offen
            </StatusPill>
          )}
        </span>
        {filtered && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange(DEFAULT_FRAME_REVIEW_FILTERS)}
          >
            <FilterX className="h-4 w-4" aria-hidden="true" />
            Filter zurücksetzen
          </Button>
        )}
      </div>
    </div>
  );
}
